import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, useToast } from './ui';
import { api } from '../lib/api';
import AgentMarkdown from './AgentMarkdown';

/**
 * Deal Radar — on-demand run of the overnight signal sweep (loans past due /
 * maturing, leases expiring, fresh listings, new properties) with the AI-ranked
 * "Top 5 actions today" digest. The same engine emails this at 7am daily.
 */
export default function DealRadarCard() {
  const [busy, setBusy] = useState(false);
  const [digest, setDigest] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const { toast } = useToast();

  const run = async () => {
    setBusy(true);
    try {
      const res: any = await api.agentDealRadar();
      setDigest(res.digest || null);
      setCounts(res.counts || null);
      if (!res.digest) toast.info('Radar is quiet', 'No signals found right now.');
    } catch (e: any) {
      toast.error('Deal Radar failed', e?.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!digest) return;
    try {
      await navigator.clipboard.writeText(digest);
      toast.success('Digest copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const COUNT_LABELS: Array<[string, string]> = [
    ['loansPastDue', 'past due'],
    ['loansMaturingSoon', 'maturing 120d'],
    ['leasesExpiringSoon', 'leases 12mo'],
    ['freshListings', 'new listings'],
    ['newProperties', 'new props'],
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>Deal Radar</CardTitle>
            <p className="text-xs text-gray-400 mt-0.5">
              Overnight signal sweep of your own data — loans, leases, listings. Emailed daily at 7am.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            {digest && !busy && (
              <Button variant="outline" size="sm" onClick={copy}>Copy</Button>
            )}
            <Button variant="outline" size="sm" onClick={run} disabled={busy}>
              {busy ? 'Scanning…' : digest ? 'Re-run' : '✨ Run radar'}
            </Button>
          </div>
        </div>
        {counts && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {COUNT_LABELS.filter(([k]) => (counts[k] ?? 0) > 0).map(([k, label]) => (
              <span key={k} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {counts[k]} {label}
              </span>
            ))}
          </div>
        )}
      </CardHeader>
      {(busy || digest) && (
        <CardContent>
          {busy ? (
            <div className="flex items-center gap-3 py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500" />
              <p className="text-sm text-gray-500">Sweeping loans, leases and listings… usually 20–40 seconds.</p>
            </div>
          ) : digest ? (
            <AgentMarkdown text={digest} />
          ) : null}
        </CardContent>
      )}
    </Card>
  );
}
